import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2,
  Users,
  LogIn,
  ShieldCheck,
  LockKeyhole,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { API } from '../App';


const ROLE_LABELS = {
  coach: 'Coach',
  assistant_coach: 'Assistant Coach',
  nutritionist: 'Nutritionist',
  healthcare_professional: 'Healthcare Professional',
};


export default function JoinOrg({ user }) {
  const { code } = useParams();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [choices, setChoices] = useState({});


  useEffect(() => {
    setLoading(true);
    setError(null);

    axios
      .get(`${API}/orgs/invite-info/${code}`)
      .then((res) => {
        const nextInfo = res.data;

        setInfo(nextInfo);

        const initial = {};

        for (
          const recipient
          of nextInfo?.consent?.recipients || []
        ) {
          initial[recipient.user_id] = [
            ...(recipient.required_scopes || []),
          ];
        }

        setChoices(initial);
      })
      .catch((e) => {
        setError(
          e.response?.data?.detail
          || 'Invite not found'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [code]);


  const isAthleteInvite = (
    info?.role === 'athlete'
  );


  const recipients = useMemo(
    () => (
      info?.consent?.recipients
      || []
    ),
    [info],
  );


  const scopeDefinitions = (
    info?.consent?.scope_definitions
    || {}
  );


  const toggleScope = (
    recipient,
    scope,
  ) => {
    const required = (
      recipient.required_scopes
      || []
    ).includes(scope);

    if (required) {
      return;
    }

    setChoices((current) => {
      const existing = new Set(
        current[recipient.user_id]
        || []
      );

      if (existing.has(scope)) {
        existing.delete(scope);
      } else {
        existing.add(scope);
      }

      return {
        ...current,
        [recipient.user_id]: [
          ...existing,
        ],
      };
    });
  };


  const handleAccept = async () => {
    if (
      isAthleteInvite
      && !acknowledged
    ) {
      toast.error(
        'Review the privacy disclosure before joining.'
      );

      return;
    }

    setJoining(true);

    try {
      const payload = {
        invite_code: code,
      };

      if (isAthleteInvite) {
        payload.consent_acknowledged = true;

        payload.consent_version = (
          info?.consent?.consent_version
        );

        payload.consents = recipients.map(
          (recipient) => ({
            recipient_user_id:
              recipient.user_id,

            scopes:
              choices[recipient.user_id]
              || [],
          })
        );
      }

      const res = await axios.post(
        `${API}/orgs/join`,
        payload,
      );

      localStorage.removeItem(
        'pending_invite_code'
      );

      toast.success(
        `Joined ${res.data.organization.name}!`
      );

      navigate('/teams');
    } catch (e) {
      toast.error(
        e.response?.data?.detail
        || 'Could not accept invite.'
      );
    } finally {
      setJoining(false);
    }
  };


  const handleSignIn = () => {
    localStorage.setItem(
      'pending_invite_code',
      code,
    );

    navigate('/auth');
  };


  const decline = () => {
    localStorage.removeItem(
      'pending_invite_code'
    );

    navigate(
      user
        ? '/teams'
        : '/'
    );
  };


  return (
    <div
      className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900 flex items-center justify-center p-4"
      data-testid="join-org-page"
    >
      <Card className="glass border-stone-800 p-6 sm:p-8 w-full max-w-2xl">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-amber-900 to-amber-950 border-2 border-amber-700 mb-4 shadow-lg shadow-amber-900/40">
            <Users className="w-8 h-8 text-amber-400" />
          </div>
        </div>

        {loading ? (
          <Loader2 className="w-6 h-6 text-amber-400 animate-spin mx-auto" />
        ) : error ? (
          <div className="text-center">
            <h1 className="text-xl font-bold text-white tracking-wider mb-2">
              INVITE NOT FOUND
            </h1>

            <p className="text-stone-400 text-sm mb-6">
              {error}
            </p>

            <Button
              onClick={() => navigate('/')}
              className="bg-stone-800 hover:bg-stone-700 text-stone-200"
            >
              Go Home
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center">
              <h1 className="text-xl font-bold text-white tracking-wider mb-1">
                YOU&apos;RE INVITED
              </h1>

              <p className="text-stone-300 mb-1">
                <span className="text-amber-400 font-bold">
                  {info.org_name}
                </span>

                {' '}invited you to join

                {info.team_name ? (
                  <>
                    {' '}team{' '}
                    <span className="text-amber-400 font-bold">
                      {info.team_name}
                    </span>
                  </>
                ) : (
                  ' their organization'
                )}

                {info.role && info.role !== 'athlete' ? (
                  <>
                    {' '}as{' '}
                    <span className="text-amber-400 font-bold">
                      {info.role.replaceAll('_', ' ')}
                    </span>
                  </>
                ) : null}
                .
              </p>

              <p className="text-xs text-stone-500 mb-6">
                Free for you ? the organization covers your access.
              </p>
            </div>

            {info.status !== 'pending' ? (
              <p className="text-blood-400 text-sm font-bold uppercase tracking-wider text-center">
                This invite is {info.status}.
              </p>
            ) : (
              <>
                {isAthleteInvite && (
                  <div
                    className="mb-6 text-left"
                    data-testid="athlete-consent-panel"
                  >
                    <div className="rounded-lg border border-amber-900/70 bg-amber-950/20 p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <ShieldCheck className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />

                        <div>
                          <h2 className="text-sm font-bold text-white tracking-wide">
                            YOUR PRIVACY CHOICES
                          </h2>

                          <p className="text-xs text-stone-400 mt-1 leading-relaxed">
                            Organization membership does not automatically give
                            staff access to your sensitive fitness information.
                            Permissions are granted to the named people below,
                            by data category.
                          </p>
                        </div>
                      </div>
                    </div>

                    {recipients.length === 0 ? (
                      <Card className="border-stone-800 bg-stone-950/40 p-4">
                        <div className="flex items-start gap-3">
                          <LockKeyhole className="w-4 h-4 text-emerald-400 mt-0.5" />

                          <div>
                            <p className="text-sm font-medium text-white">
                              No professional currently has a data-sharing request.
                            </p>

                            <p className="text-xs text-stone-500 mt-1">
                              Joining will not grant sensitive athlete-data
                              access to an organization administrator or other
                              staff merely because you are a member.
                            </p>
                          </div>
                        </div>
                      </Card>
                    ) : (
                      <div className="space-y-4">
                        {recipients.map((recipient) => (
                          <Card
                            key={recipient.user_id}
                            className="border-stone-800 bg-stone-950/40 p-4"
                            data-testid={`consent-recipient-${recipient.user_id}`}
                          >
                            <div className="mb-3">
                              <p className="text-white font-bold">
                                {recipient.username}
                              </p>

                              <p className="text-xs uppercase tracking-wider text-amber-400">
                                {ROLE_LABELS[recipient.role] || recipient.role}
                              </p>
                            </div>

                            <div className="space-y-2">
                              {(recipient.allowed_scopes || []).map((scope) => {
                                const required = (
                                  recipient.required_scopes
                                  || []
                                ).includes(scope);

                                const allowed = (
                                  choices[recipient.user_id]
                                  || []
                                ).includes(scope);

                                const definition = (
                                  scopeDefinitions[scope]
                                  || {}
                                );

                                return (
                                  <label
                                    key={scope}
                                    className="flex items-start gap-3 rounded-md border border-stone-800 bg-stone-900/50 p-3 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={allowed}
                                      disabled={required}
                                      onChange={() => toggleScope(
                                        recipient,
                                        scope,
                                      )}
                                      className="mt-1 accent-amber-600"
                                      data-testid={`consent-${recipient.user_id}-${scope}`}
                                    />

                                    <div className="flex-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium text-stone-200">
                                          {definition.label || scope}
                                        </span>

                                        {required && (
                                          <span className="text-[10px] uppercase tracking-wider text-amber-400">
                                            Required for this service
                                          </span>
                                        )}
                                      </div>

                                      <p className="text-xs text-stone-500 mt-1">
                                        {definition.description}
                                      </p>
                                    </div>

                                    <span
                                      className={`text-xs font-bold uppercase tracking-wider ${
                                        allowed
                                          ? 'text-emerald-400'
                                          : 'text-stone-500'
                                      }`}
                                    >
                                      {allowed ? 'Allow' : 'Private'}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}

                    <label className="flex items-start gap-3 mt-4 rounded-lg border border-stone-700 p-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                        className="mt-1 accent-amber-600"
                        data-testid="consent-acknowledgement"
                      />

                      <span className="text-xs text-stone-300 leading-relaxed">
                        I have reviewed who can receive my information and
                        which categories I am allowing. I understand I can
                        later change or revoke optional permissions.
                      </span>
                    </label>

                    <p className="text-xs text-stone-500 mt-3 leading-relaxed">
                      By clicking{' '}
                      <strong className="text-stone-300">
                        Allow &amp; Join
                      </strong>
                      , you agree to share only the selected data categories
                      with the named professionals shown above. If a required
                      category is not acceptable to you, decline the
                      invitation instead.
                    </p>
                  </div>
                )}

                {user ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={decline}
                      variant="outline"
                      disabled={joining}
                      className="sm:flex-1 border-stone-700 bg-stone-900 text-stone-300 hover:bg-stone-800"
                      data-testid="join-decline-btn"
                    >
                      Decline Invitation
                    </Button>

                    <Button
                      onClick={handleAccept}
                      disabled={
                        joining
                        || (
                          isAthleteInvite
                          && !acknowledged
                        )
                      }
                      className="sm:flex-1 bg-amber-700 hover:bg-amber-600 text-white font-bold py-5"
                      data-testid="join-accept-btn"
                    >
                      {joining ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        isAthleteInvite
                          ? 'Allow & Join'
                          : 'Accept & Join'
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleSignIn}
                    className="w-full bg-amber-700 hover:bg-amber-600 text-white font-bold py-5"
                    data-testid="join-signin-btn"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In / Create Free Account
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
